import React from 'react';
import { motion } from 'framer-motion';
import {
  Code2,
  Smartphone,
  Wrench,
  MessageSquare,
  CreditCard,
  ShoppingCart,
} from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Code2,
      title: 'Web Development',
      description: 'Modern, responsive web applications built with the latest technologies',
    },
    {
      icon: Smartphone,
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile solutions for iOS and Android',
    },
    {
      icon: Wrench,
      title: 'Custom EPR Software',
      description: 'Tailored software solutions designed for your specific business needs',
    },
    {
      icon: MessageSquare,
      title: 'SMS & WhatsApp',
      description: 'Automated business communication and lead management through SMS and WhatsApp APIs',
    },
    {
      icon: CreditCard,
      title: 'Payment Integration',
      description: 'Secure payment gateways and financial system integration',
    },
    {
      icon: ShoppingCart,
      title: 'Marketplace Development',
      description: 'Complete e-commerce platforms and digital marketplace solutions',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6 },
    },
  };

  return (
    <section id="services" className="py-20 bg-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-dark mb-4">
            Our <span className="text-primary">Services</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Comprehensive digital solutions to transform your business and accelerate growth
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{
                  y: -8,
                  boxShadow: '0 20px 40px rgba(229, 57, 53, 0.15)',
                }}
                className="bg-white rounded-xl p-8 transition-all duration-300 shadow-soft hover:shadow-soft-lg border border-gray-100 group"
              >
                <motion.div
                  className="inline-block p-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg mb-4 group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300"
                  whileHover={{ rotate: 15, scale: 1.15 }}
                >
                  <Icon size={28} className="text-primary" />
                </motion.div>

                <h3 className="text-xl font-bold text-dark mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>

                <a
                  href={`/contact?subject=${encodeURIComponent(`Inquiry about ${service.title}`)}`}
                  className="mt-4 flex items-center text-primary font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  Learn more →
                </a>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
